/**
 * Invite Accept Page (RBAC Phase 2 — Backend-wired)
 * 
 * Route: /register?invite=XYZ (or /invite/accept?token=XYZ)
 * 
 * Flow:
 * 1. User clicks invite link → validates token against backend
 * 2. Shows invitation details (role, clinic)
 * 3. New user: creates account via POST /api/auth/unified/register
 * 4. Existing user: same endpoint handles appending clinic membership
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Building2, CheckCircle, XCircle, Clock, AlertTriangle,
  Mail, User, Shield, Lock, Eye, EyeOff, ChevronLeft
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import api, { clinicLeadAPI } from '../utils/api';


// OF-001D #3 — Legacy invite-token role ids are normalized to
// canonical provider role ids; labels are resolved via `roleLabel()`.
// No local label literals allowed for canonical roles.
import { roleLabel } from '../utils/canonicalRoleLabels';

const LEGACY_INVITE_ROLE_TO_CANONICAL = {
  clinic_admin:        'provider_admin',
  clinic_sales:        'provider_sales_rep',
  sales_rep:           'provider_sales_rep',
  clinic_after_sales:  'provider_after_sales_rep',
  doctor:              'provider_doctor',
  clinic_supervisor:   'provider_coordinator',
  clinic_representative:'provider_staff',
};

const ROLE_LABELS = new Proxy({}, {
  get(_, key) {
    const canonical = LEGACY_INVITE_ROLE_TO_CANONICAL[key] || key;
    return roleLabel(canonical);
  },
});

const InviteAcceptPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite') || searchParams.get('inviteToken') || searchParams.get('token');

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('loading'); // loading, invalid, expired, signup, success
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (!inviteToken) {
      setError('No invitation token provided');
      setStep('invalid');
      setLoading(false);
      return;
    }

    const validateToken = async () => {
      try {
        const res = await api.get(`/auth/unified/invite/${inviteToken}`);
        if (false) { /* axios throws on error */
          setError('Invalid or expired invitation link');
          setStep('invalid');
          setLoading(false);
          return;
        }
        const data = res.data;
        const inv = data.invite || data;

        if (!inv || !inv.email) {
          setError('Invalid invitation');
          setStep('invalid');
          setLoading(false);
          return;
        }

        // Check status
        const status = inv.status?.toLowerCase();
        if (status === 'expired' || (inv.expires_at && new Date(inv.expires_at) < new Date())) {
          setError('This invitation has expired');
          setStep('expired');
          setInvitation(inv);
          setLoading(false);
          return;
        }
        if (status === 'cancelled' || status === 'revoked') {
          setError('This invitation has been cancelled');
          setStep('invalid');
          setLoading(false);
          return;
        }
        if (status === 'accepted') {
          setError('This invitation has already been accepted');
          setStep('success');
          setInvitation(inv);
          setLoading(false);
          return;
        }

        setInvitation(inv);
        setStep(inv.existing_user ? 'existing' : 'signup');
        setLoading(false);
      } catch {
        // PATIENT-INVITE-EMAIL-01 — the staff-invite reader above only knows
        // `kind='clinic_staff'`. A patient invited by their own provider holds a
        // `kind='patient_lead'` invitation, validated by the EXISTING public
        // endpoint. Same page, same acceptance UI — no second invitation system.
        try {
          const { data } = await clinicLeadAPI.validateInvite(inviteToken);
          if (data?.valid) {
            setInvitation({
              kind: 'patient_lead',
              token: inviteToken,
              email: data.patient_email,
              name: data.patient_name || '',
              clinic_name: data.clinic_name || '',
              expires_at: data.expires_at,
            });
            setFormData(f => ({ ...f, name: data.patient_name || '' }));
            setStep('signup');
            setLoading(false);
            return;
          }
          setError('Invalid invitation');
          setStep('invalid');
          setLoading(false);
          return;
        } catch (e2) {
          const code = e2?.response?.status;
          if (code === 410) {
            setError('This invitation has expired or has already been used');
            setStep('expired');
            setLoading(false);
            return;
          }
        }
        setError('Failed to validate invitation');
        setStep('invalid');
        setLoading(false);
      }
    };

    validateToken();
  }, [inviteToken]);

  const handleAccept = async () => {
    if (step === 'signup') {
      if (!formData.name.trim()) {
        toast({ title: 'Error', description: 'Please enter your name', variant: 'destructive' });
        return;
      }
      if (!formData.password || formData.password.length < 6) {
        toast({ title: 'Error', description: 'Password must be at least 6 characters', variant: 'destructive' });
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
        return;
      }
    }

    setSubmitting(true);
    try {
      let res;
      if (invitation?.kind === 'patient_lead') {
        // Canonical patient-lead acceptance: creates the patient identity and
        // links it to the SAME provider-owned lead (and therefore its offer).
        res = await clinicLeadAPI.acceptInvite(invitation.token, {
          name: formData.name || invitation.email.split('@')[0],
          password: formData.password,
        });
      } else if (step === 'existing') {
        // Existing user: Use the invite/accept endpoint to ADD clinic membership
        // User must be logged in first
        const loginRes = await api.post(`/auth/unified/login`, {
          email: invitation.email,
          password: formData.password,
        });
        const loginData = loginRes.data;
        if (!loginData.success) {
          toast({ title: 'Error', description: 'Invalid password. Please try again.', variant: 'destructive' });
          setSubmitting(false);
          return;
        }
        // Now accept the invite with the authenticated token
        res = await api.post(`/auth/unified/invite/accept`, {
          invite_token: invitation.token,
        });
      } else {
        // New user: Register WITH invite_token for membership creation
        res = await api.post(`/auth/unified/register`, {
          email: invitation.email,
          password: formData.password,
          name: formData.name || invitation.email.split('@')[0],
          invite_token: invitation.token,
        });
      }
      const data = res.data;

      if (data.success) {
        // FIND-H-08: token no longer surfaced
        setStep('success');
        toast({
          title: invitation?.kind === 'patient_lead' ? 'Welcome to TreatX!' : 'Welcome to the team!',
          description: invitation?.kind === 'patient_lead'
            ? `Your account is ready${invitation.clinic_name ? ` — ${invitation.clinic_name} can now share your offer with you` : ''}.`
            : `You've been added as ${ROLE_LABELS[invitation.role] || invitation.role}`,
        });
      } else {
        toast({ title: 'Error', description: data.error || 'Failed to accept invitation', variant: 'destructive' });
      }
    } catch (e) {
      const errMsg = e.response?.data?.detail || e.response?.data?.error || 'Failed to process invitation';
      toast({ title: 'Error', description: errMsg, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const roleName = invitation
    ? (invitation.kind === 'patient_lead' ? 'Patient' : (ROLE_LABELS[invitation.role] || invitation.role))
    : '';

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <Clock className="h-10 w-10 text-teal-600 mx-auto animate-pulse" />
            <p className="mt-4 text-gray-600">Validating invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid
  if (step === 'invalid') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 flex items-center justify-center">
        <Card data-testid="invite-invalid" className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto" />
            <h2 className="text-xl font-bold mt-4 text-gray-900">Invalid Invitation</h2>
            <p className="text-gray-600 mt-2">{error}</p>
            <Button onClick={() => navigate('/login')} className="mt-6">
              <ChevronLeft className="h-4 w-4 mr-1" /> Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Expired
  if (step === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 flex items-center justify-center">
        <Card data-testid="invite-expired" className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto" />
            <h2 className="text-xl font-bold mt-4 text-gray-900">Invitation Expired</h2>
            <p className="text-gray-600 mt-2">This invitation has expired. Please ask your admin to resend.</p>
            <Button onClick={() => navigate('/login')} className="mt-6">
              <ChevronLeft className="h-4 w-4 mr-1" /> Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 flex items-center justify-center">
        <Card data-testid="invite-success" className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
            <h2 className="text-xl font-bold mt-4 text-gray-900">Welcome to the Team!</h2>
            <p className="text-gray-600 mt-2">
              You've been added as <span className="font-semibold">{roleName}</span>
              {invitation?.clinic_id && ' to the clinic'}.
            </p>
            <Button onClick={() => navigate('/clinic/admin')} className="mt-6">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Signup / Existing User form
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 flex items-center justify-center p-4">
      <Card data-testid="invite-accept-form" className="max-w-md w-full">
        <CardHeader className="text-center pb-4">
          <div className="w-14 h-14 mx-auto rounded-full bg-teal-100 flex items-center justify-center mb-3">
            <Building2 className="h-7 w-7 text-teal-600" />
          </div>
          <CardTitle className="text-xl">You're Invited!</CardTitle>
          <p className="text-gray-500 text-sm mt-1">
            {step === 'existing' ? 'Link your existing account to join' : 'Create your account to join'}
          </p>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Invite details */}
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4 text-teal-600" />
              <span className="text-gray-700">{invitation?.email}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Shield className="h-4 w-4 text-teal-600" />
              <span className="text-gray-700">Role: </span>
              <Badge className="bg-teal-100 text-teal-700">{roleName}</Badge>
            </div>
            {invitation?.clinic_id && (
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="h-4 w-4 text-teal-600" />
                <span className="text-gray-700">Clinic: {invitation.clinic_id}</span>
              </div>
            )}
          </div>

          {step === 'signup' && (
            <>
              <div>
                <Label>Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    data-testid="invite-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your full name"
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Label>Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    data-testid="invite-password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Min 6 characters"
                    className="pl-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                  </button>
                </div>
              </div>
              <div>
                <Label>Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    data-testid="invite-confirm-password-input"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="Confirm password"
                    className="pl-10"
                  />
                </div>
              </div>
            </>
          )}

          {step === 'existing' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
              <p>Your account already exists. Accepting will link you to this clinic with the assigned role.</p>
              <div className="mt-2">
                <Label>Password (to confirm identity)</Label>
                <Input
                  data-testid="invite-existing-password-input"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter your password"
                  className="mt-1"
                />
              </div>
            </div>
          )}

          <Button
            data-testid="invite-accept-btn"
            onClick={handleAccept}
            disabled={submitting}
            className="w-full"
          >
            {submitting ? 'Joining...' : step === 'existing' ? 'Link & Join' : 'Create Account & Join'}
          </Button>

          <div className="text-center">
            <Button variant="link" onClick={() => navigate('/login')} className="text-gray-500">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back to Login
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InviteAcceptPage;
